<template>
  <div>
    <Header />

    <div v-if="newModelWorkflow" id="model-header">
      <EditableText tag="h1" id="model-title"
        :text="modelTitle"
        @update:text="updateTitle($event)">
      </EditableText>

      <EditableText tag="h1" id="model-subtitle"
        :multiline="true"
        :text="modelDescription"
        @update:text="updateDescription($event)">
      </EditableText>

      <b class="model-id-text">Model #{{modelID}}</b>
    </div>
    <div v-else id="model-header">
      <h1 id="model-title">{{ modelTitle }}</h1>
      <h2 id="model-subtitle">{{ modelDescription }}</h2>
      <b class="model-id-text" v-if="modelID">Model #{{modelID}}</b>
    </div>


    <div v-if="!newModelWorkflow">
      <VButton @click="forkModel">Fork Model</VButton>
      &nbsp;
    </div>

    <!-- Model DAG Visualization -->
    <div v-if="isLoaded" id="model-view">
      <ModelDAGVisualization
        :graphWidth="850"
        :graphHeight="475"
        :key="`dag${modelID}${newModelWorkflow}`"
      />

      <!-- Save Model Button (for newly created models only) -->
      <VButton v-if="newModelWorkflow" @click="updateModel">
        Save Model
      </VButton>

      <h3>{{ (label.length > 1) ? '' : label[0] }}</h3>

      <div v-if="method && !Array.isArray(method)">
        <MethodSelector
          :method="method" 
          :inputMapping="methodInputs"
          :availableComponents="componentInputs"
          @changeMethod="updateMethod($event)"
          @adjustParams="updateInputMapping($event)"
        />
        <!-- :scenarios="scenarios" @adjustParams="updateScenario($event)" -->

        <VButton @click="addAdjustment">Add Adjustment</VButton>
      </div>
      <div v-else-if="method">
        <div v-for="(m, index) in method" :key="index">
          {{ label[index] }}

          <MethodSelector
            v-if="m"
            :method="m"
            :inputMapping="methodInputs[index]"
            :availableComponents="componentInputs[index]"
            @changeMethod="updateMethod({...$event, overwriteLabel: label[index]})"
            @adjustParams="updateInputMapping({...$event, overwriteLabel: label[index]})"
          />
          <!-- :scenarios="scenarios[index]" @adjustParams="updateScenario({...$event, overwriteLabel: label[index]})" -->
        </div>

        <VButton @click="addAdjustment({overwriteLabel: label[index]})">
          Add Adjustment
        </VButton>
      </div>
      <div v-else-if="componentInputs.length">
        <!-- TODO: Remove dataVersion as a hack --->
        <OverrideComponentValue
          v-if="seriesType === 'bar'"
          @updateDataVersion="dataVersion++"
        />
        <br/>
        <VButton @click="addAdjustment">Add Adjustment</VButton>
      </div>
      <div v-else>
        <!-- TODO: Remove dataVersion as a hack --->
        <OverrideComponentValue
          v-if="seriesType === 'bar'"
          @updateDataVersion="dataVersion++"
        />
      </div>

      <div v-if="data && isLoaded">
        <!-- TODO: Remove dataVersion as a hack --->
        <TimeSeriesGraph v-if="seriesType === 'line'"
          :graphWidth="optimalGraphWidth"
          :key="'graph'+modelID+dataVersion"
          :datas="data"
          :labels="label"
          :showTitle="false"
        />
        <BarGraph v-else-if="seriesType === 'bar'"
          :graphWidth="optimalGraphWidth"
          :key="'graph2'+modelID+dataVersion"
          :datas="data"
          :labels="label"
          :showTitle="false"
        />
        <span v-else>Graph for the selected DataSeries type is not yet supported</span>
      </div>
    </div>

    <br />
    <hr />

    <EmailCollection />

    <Footer />
  </div>
</template>

<script>
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import TimeSeriesGraph from '@/components/model/TimeSeriesGraph';
import BarGraph from '@/components/graph/BarGraph';
import EmailCollection from '@/components/layout/EmailCollection';
import ModelDAGVisualization from '@/components/model/ModelDAGVisualization';
import MethodSelector from '@/components/model/MethodSelector';
import EditableText from '@/components/ui/EditableText';

import { FixedSeries, TimeSeries } from '@/js/model/DataSeries';
import { makeArray } from '@/js/model/Util';
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';
import VButton from '../components/ui/VButton.vue';
import OverrideComponentValue from '../components/model/OverrideComponentValue.vue';


export default {
  name: 'ModelView',
  components: {
    Header,
    Footer,
    TimeSeriesGraph,
    BarGraph,
    ModelDAGVisualization,
    MethodSelector,
    EmailCollection,
    EditableText,
    VButton,
    OverrideComponentValue
  },
  props: {
    modelID: Number,
    templateName: String
  },
  data: function() { // TODO: Remove dataVersion as a hack
    return { dataVersion: 0 };
  },
  metaInfo() {
    return {
      title: `${this.modelTitle} Model`,
      meta: [
        // Twitter Sharing Metadata
        {'name': 'twitter:title', 'content': `${this.modelTitle} on Rebar`},
        {'name': 'twitter:description',
         'content': `Use, discuss, and model ${this.modelTitle} on Rebar`},
        // Generic Sharing Metadata (iMessage, etc.)
        {'property': 'og:url', 'content': `https://www.rebar.ai/model/${this.modelID}`},
        {'property': 'og:title', 'content': `${this.modelTitle} models on Rebar`},
        {'property': 'og:description',
         'content': `Use, discuss, and model ${this.modelTitle} on Rebar`},
      ]
    }
  },
  watch: {
    modelID: {
      immediate: true,
      handler() { this.load(); }
    }
  },
  computed: {
    ...mapState(['modelTitle','modelDescription','model','isLoaded','label','updateKey']),
    ...mapGetters(['component','componentInputs','data','method','methodInputs','scenarios']),
    newModelWorkflow: function() {
      return this.updateKey !== null;
    },
    optimalGraphWidth: function() {
      return Math.max(400, Math.min(850, window.innerWidth));
    },
    seriesType: function() {
      let dataArray = makeArray(this.data);
      if (dataArray.every(d => d instanceof TimeSeries)) {
        return 'line';
      } else if (dataArray.every(d => d instanceof FixedSeries)) {
        return 'bar';
      } else {
        return 'mixed';
      }
    }
  },
  methods: {
    ...mapMutations(['selectLabels','updateInputMapping','updateTitle',
      'updateDescription','updateMethod','updateScenario','addAdjustment']),
    ...mapActions(['saveModel','updateModel','loadTemplate','loadModel']),
    load() {
      if (this.templateName) {
        this.loadTemplate(this.templateName);
      } else {
        this.loadModel(this.modelID);
      }
    },
    getUpdateCookie() {
      this.cookieUpdateKey = this.$cookies.get(`${this.modelID}:update-key`);
    },
    forkModel() {
      this.saveModel().then(
        (modelID) => {
          this.$router.push({'name': 'Model View', 'params': {'modelID': modelID}});
        });
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
#model-view {
  margin-left: auto;
  margin-right: auto;
  max-width: 90%;
}

/* Model Header */
#model-header {
  margin: 20px 0;
}
#model-header > * {
  margin: 0px;
  width: 50%;
  margin-left: auto;
  margin-right: auto;
}
#model-title {
  font-size: 1.3em;
}
#model-subtitle {
  font-size: 1em;
  font-weight: normal;
}
.model-id-text {
  color: gray;
  font-size: .8em;
}
</style>
